import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { dateTime } from '../lib/format';
import { toast } from '../lib/toast';
import { errorFields } from '../lib/api-error';
import {
  AREA_LABELS,
  canHoldPermissions,
  PERMISSION_AREAS,
  ROLE_LABELS,
  ROLES,
  type PermissionArea,
  type Role,
} from '../lib/permissions';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  role: Role;
  isActive: boolean;
  permissions: string[] | null;
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export function UsersPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<AdminUserRow[]>('/admin/users'),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin-users'] });

  return (
    <>
      <PageHeader
        title="Kullanıcılar"
        description="Kullanıcı yönetimi yalnızca yöneticilere açıktır — yetki yükseltilebilen tek yer burasıdır."
        actions={<Button onClick={() => setCreating(true)}>Yeni kullanıcı</Button>}
      />

      <Card>
        {users.isPending ? (
          <div className="px-4 py-10 text-center">
            <Spinner size={20} />
          </div>
        ) : users.isError ? (
          <EmptyState title="Kullanıcılar yüklenemedi" />
        ) : users.data.length === 0 ? (
          <EmptyState title="Kayıtlı kullanıcı yok" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Ad</Th>
                <Th>E-posta</Th>
                <Th>Rol</Th>
                <Th>Durum</Th>
                <Th>Son giriş</Th>
                <Th align="right">İşlem</Th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((u) => (
                <tr key={u.id}>
                  <Td>
                    {u.name}
                    {u.id === me?.id && (
                      <span className="ml-1 text-xs text-[var(--c-ink-faint)]">(siz)</span>
                    )}
                  </Td>
                  <Td>{u.email}</Td>
                  <Td>
                    <Badge tone={u.role === 'ADMIN' ? 'brand' : 'neutral'}>
                      {ROLE_LABELS[u.role]}
                    </Badge>
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <Badge tone={u.isActive ? 'ok' : 'danger'}>
                        {u.isActive ? 'aktif' : 'devre dışı'}
                      </Badge>
                      {u.twoFactorEnabled && <Badge tone="brand">2FA</Badge>}
                    </div>
                  </Td>
                  <Td>{u.lastLoginAt ? dateTime(u.lastLoginAt) : 'hiç girmedi'}</Td>
                  <Td align="right">
                    <Button variant="secondary" size="sm" onClick={() => setEditing(u)}>
                      Düzenle
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            invalidate();
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          isSelf={editing.id === me?.id}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────── izin seçici

function PermissionPicker({
  role,
  value,
  onChange,
}: {
  role: Role;
  value: string[] | null;
  onChange: (next: string[] | null) => void;
}) {
  /*
   * ⚠ İzin listesi rol varsayılanlarına EKLENMEZ, onların YERİNE geçer
   * (AreaPermissionGuard: `permissions ?? DEFAULT_AREAS_BY_ROLE[role]`).
   * Bu yüzden form "sıfırdan seç" mantığıyla çalışır ve boş bırakmak
   * "rol varsayılanı" demektir — "hiçbir yetki yok" değil.
   */
  if (!canHoldPermissions(role)) {
    return (
      <p className="text-xs text-[var(--c-ink-soft)]">
        Yönetici tüm alanlara erişir; izin listesi dikkate alınmaz.
      </p>
    );
  }

  const custom = value !== null;

  return (
    <div>
      <Checkbox
        label="Özel izin listesi kullan"
        hint="Kapalıyken rolün varsayılan alanları geçerlidir. Açıldığında liste varsayılanların YERİNE geçer."
        checked={custom}
        onChange={(e) => onChange(e.target.checked ? [] : null)}
      />
      {custom && (
        <div className="mt-2 grid grid-cols-2 gap-x-4 rounded-[var(--r-md)] border border-[var(--c-border)] p-3">
          {PERMISSION_AREAS.map((area: PermissionArea) => (
            <Checkbox
              key={area}
              label={AREA_LABELS[area]}
              checked={value?.includes(area) ?? false}
              onChange={(e) => {
                const set = new Set(value ?? []);
                if (e.target.checked) set.add(area);
                else set.delete(area);
                onChange([...set]);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────── oluşturma

function CreateUserModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('ACCOUNTANT');
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [invalid, setInvalid] = useState<Set<string>>(new Set());

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/users', {
        name: name.trim(),
        email: email.trim(),
        password,
        role,
        // `permissions: null` göndermek yerine ALANI HİÇ göndermiyoruz:
        // DTO'da `permissions?: string[]` var ve null tip hatası verirdi.
        ...(permissions !== null ? { permissions } : {}),
      }),
    onSuccess: () => {
      toast.success('Kullanıcı oluşturuldu.');
      onDone();
    },
    onError: (err) => {
      setInvalid(errorFields(err));
      toast.error(err);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Yeni kullanıcı"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={create.isPending} onClick={() => create.mutate()}>
            Oluştur
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Ad"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={invalid.has('name') ? 'En az 2 karakter olmalı.' : null}
        />
        <Input
          label="E-posta"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={invalid.has('email') ? 'Geçerli bir e-posta girin.' : null}
          hint="Gmail nokta ve +etiket varyantları aynı kutu sayılır; ikinci hesap açılamaz."
        />
        <Input
          label="Şifre"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={invalid.has('password') ? 'Şifre en az 12 karakter olmalı.' : null}
          hint="En az 12 karakter."
        />
        <Select label="Rol" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </Select>
        <PermissionPicker role={role} value={permissions} onChange={setPermissions} />
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────── düzenleme

function EditUserModal({
  user,
  isSelf,
  onClose,
  onDone,
}: {
  user: AdminUserRow;
  isSelf: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<Role>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [permissions, setPermissions] = useState<string[] | null>(user.permissions);

  const update = useMutation({
    mutationFn: () => {
      /*
       * YALNIZCA değişen alanlar gönderilir. Sunucu `role`, `isActive:false`
       * veya `permissions` alanlarından biri geldiğinde tokenVersion'ı artırır
       * ve o kullanıcının TÜM oturumlarını düşürür — sadece adı düzeltirken
       * kimseyi sistemden atmayalım.
       */
      const patch: Record<string, unknown> = {};
      if (name.trim() !== user.name) patch.name = name.trim();
      if (role !== user.role) patch.role = role;
      if (isActive !== user.isActive) patch.isActive = isActive;
      if (JSON.stringify(permissions) !== JSON.stringify(user.permissions)) {
        patch.permissions = permissions;
      }
      if (Object.keys(patch).length === 0) return Promise.resolve(null);
      return api.patch(`/admin/users/${user.id}`, patch);
    },
    onSuccess: (res) => {
      toast.success(res === null ? 'Değişiklik yok.' : 'Kullanıcı güncellendi.');
      onDone();
    },
    onError: (err) => toast.error(err),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={user.email}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={update.isPending} onClick={() => update.mutate()}>
            Kaydet
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Ad" value={name} onChange={(e) => setName(e.target.value)} />

        <Select
          label="Rol"
          value={role}
          disabled={isSelf}
          hint={isSelf ? 'Kendi rolünüzü değiştiremezsiniz.' : undefined}
          onChange={(e) => setRole(e.target.value as Role)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </Select>

        <Checkbox
          label="Hesap aktif"
          disabled={isSelf}
          hint={
            isSelf
              ? 'Kendi hesabınızı devre dışı bırakamazsınız.'
              : 'Devre dışı bırakmak kullanıcının açık oturumlarını da düşürür.'
          }
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />

        <PermissionPicker role={role} value={permissions} onChange={setPermissions} />

        <p className="text-xs text-[var(--c-ink-faint)]">
          E-posta ve şifre buradan değiştirilemez. Şifreyi yalnızca kullanıcının kendisi profilinden
          değiştirebilir.
        </p>
      </div>
    </Modal>
  );
}
